import React, { useEffect, useState } from 'react';
import { businessPublicApi } from '../services/api';

interface Business {
  id: string;
  mangoId: string;
  name: string;
  description?: string | null;
  businessType?: string | null;
  industryCategory?: string | null;
  website?: string | null;
  logoUrl?: string | null;
  country?: string | null;
}

const Businesses: React.FC = () => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await businessPublicApi.list({
        limit: 50,
        search: search.trim() || undefined,
      });
      if (res?.success) {
        const list = res?.data ?? [];
        setBusinesses(Array.isArray(list) ? list : []);
        setTotal(typeof res?.total === 'number' ? res.total : list?.length ?? 0);
      } else {
        setError(res?.error || 'Failed to load businesses');
        setBusinesses([]);
      }
    } catch (err) {
      console.error('Load businesses failed:', err);
      setError('Failed to load businesses.');
      setBusinesses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusinesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount only
  }, []);

  const handleSearch = () => {
    loadBusinesses();
  };

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>🔍 Discover Businesses</h1>
      <p style={{ margin: '10px 0 25px 0', color: '#666' }}>Find Mango businesses near you</p>

      {/* Search */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '25px',
        flexWrap: 'wrap',
      }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Search businesses..."
          style={{
            flex: '1 1 280px',
            padding: '12px 16px',
            border: '1px solid #e0e0e0',
            borderRadius: '10px',
            fontSize: '14px',
          }}
        />
        <button
          onClick={handleSearch}
          style={{
            padding: '12px 24px',
            background: '#ff6b35',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
          }}
        >
          Search
        </button>
      </div>

      {error && (
        <div style={{
          background: '#fee',
          color: '#c00',
          padding: '12px 16px',
          borderRadius: '10px',
          marginBottom: '20px',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <span style={{ fontSize: '48px' }}>🔍</span>
          <p style={{ color: '#666', marginTop: '15px' }}>Loading businesses...</p>
        </div>
      ) : businesses.length === 0 ? (
        <div style={{
          background: 'white',
          padding: '60px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}>
          <span style={{ fontSize: '64px' }}>🏪</span>
          <h2 style={{ margin: '20px 0 10px 0', color: '#333' }}>No businesses found</h2>
          <p style={{ color: '#666', margin: 0 }}>
            {search ? 'Try a different search term' : 'No businesses in the directory yet'}
          </p>
        </div>
      ) : (
        <>
          <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px' }}>
            Showing {businesses.length} of {total} businesses
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
          }}>
            {businesses.map((b) => (
              <div
                key={b.id}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }}
              >
                {b.logoUrl && (
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    background: `url(${b.logoUrl}) center/cover`,
                    marginBottom: '16px',
                  }} />
                )}
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                  {b.mangoId}
                  {b.industryCategory && ` • ${b.industryCategory}`}
                </div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#333' }}>{b.name}</h3>
                {b.description && (
                  <p style={{
                    margin: '0 0 12px 0',
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  } as React.CSSProperties}>
                    {b.description}
                  </p>
                )}
                {b.website && (
                  <a
                    href={b.website.startsWith('http') ? b.website : `https://${b.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '13px',
                      color: '#ff6b35',
                      textDecoration: 'none',
                    }}
                  >
                    Visit website →
                  </a>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Businesses;
